import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import dayjs from 'dayjs';


import { Flex, FlexColumn } from 'src/components/flex';
import { ShowingIcon, SnippetsIcon } from 'src/icons';
import {
    ELeadStage,
    useUpdateLeadStage,
    ELeadStageChangeReason,
    useSmartReplies,
} from 'src/services/api';
import { useConversation } from 'src/services/api/messages/use-conversation';
import { useSmartNotifications } from 'src/services/smart-notifications/use-smart-notifications';
import SmartReplies from 'src/pages/landlord/leads/messages/components/input/SmartReplies';
import analyticsClient, { AnalyticsEvent } from 'src/services/analytics';
import { ESmartNotificationAction, ESmartNotificationType } from 'src/services/smart-notifications/types';
import { SelectLeadStage } from 'src/pages/landlord/leads/messages/components/smart-notifications/SmartNotificationsStageSelect';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context';
import { isLastMessageValidForSmartRepliesValid } from 'src/services/smart-notifications/smart-finder';
import { LEAD_STAGE_LABELS } from 'src/components/input/select-lead-stage/leadStageOptions';

import styles from './SmartNotification.module.css';


type Props = {
    openTourModal: () => void;
}

export const SmartNotifications = (props: Props) => {

    const {
        lead,
        setReplyInput,
        setIsSnippetsModalOpen,
        showings,
        messages
    } = useLeadMessagesPageContext();

    const { openTourModal } = props;

    const { data: conversation } = useConversation(lead.uuid);

    const notification = useSmartNotifications({
        lead,
        showings,
        messages
    });

    const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down('md'));
    const notifMidsize = useMediaQuery((theme: any) => theme.breakpoints.down('notificationsMidSize'));
    const notifMidsize2 = useMediaQuery((theme: any) => theme.breakpoints.down('notificationsMidSize2'));

    const {
        data: smartReplies
        // with false because we want only check if we can show smart replies, fetch performs in another place
    } = useSmartReplies(lead.uuid, undefined, false);

    const { mutate: updateLead } = useUpdateLeadStage(lead.uuid);

    const [smartRepliesVisible, setSmartRepliesVisible] = useState(false);

    const hideSmartReplies = useMemo(() => {
        return lead.stage == ELeadStage.NEW || !conversation || !isLastMessageValidForSmartRepliesValid(conversation);
    }, [lead, conversation]);

    const isWarning = notification?.type === ESmartNotificationType.warning;

    useEffect(() => {
        setSmartRepliesVisible(false);
    }, [lead.uuid]);

    /**
     * This function can be called in two scenarios:
     * 1. When the nudge notification specifies a specific stage to change to.
     * 2. When the user selects a stage from the nudge dropdown.
     */
    const handleChangeStageWithNudge = (newStage: ELeadStage, reason: ELeadStageChangeReason) => {
        analyticsClient.track(
            AnalyticsEvent.USED_SMART_NOTIFICATION_CHANGE_STAGE
        );
        updateLead({
            stage: newStage,
            reason: reason,
        });
    };

    const dispatchAction = useCallback(
        (actionType?: ESmartNotificationAction) => {
            if (!notification || !actionType) {
                return;
            }
            switch (actionType) {
                case ESmartNotificationAction.OPEN_SNIPPETS:
                    analyticsClient.track(
                        AnalyticsEvent.USED_SMART_NOTIFICATION_OPEN_SNIPPETS
                    );
                    setIsSnippetsModalOpen(true);
                    break;
                case ESmartNotificationAction.CHANGE_SPECIFIC_STAGE:
                    analyticsClient.track(
                        AnalyticsEvent.USED_SMART_NOTIFICATION_CHANGE_STAGE
                    );
                    handleChangeStageWithNudge(notification.changeToSpecificStage!, ELeadStageChangeReason.MANUAL_RECOMMEND_NUDGE);
                    break;
                case ESmartNotificationAction.OPEN_TOUR_MODAL:
                    analyticsClient.track(
                        AnalyticsEvent.USED_SMART_NOTIFICATION_SCHEDULE_TOUR
                    );
                    openTourModal();
                    break;
                default:
                    break;
            }
        },
        [notification]
    );

    const primaryAction = useMemo(() => {
        if (notification?.changeToSpecificStage) {
            return <Button variant="outlined" color={isWarning ? 'error' : 'primary'} onClick={() => dispatchAction(ESmartNotificationAction.CHANGE_SPECIFIC_STAGE)}>
                {isWarning ?
                    (<img src="/img/conversation/btn_logo_red.png" alt="image" width={15} height={15} />) :
                    (<img src="/img/conversation/btn_logo.png" alt="image" width={15} height={15} />)}
                <Typography fontSize={14} fontWeight={600} component="div" color="inherit">
                    {`Change stage to "${LEAD_STAGE_LABELS[notification?.changeToSpecificStage]}"`}
                </Typography>
            </Button>;
        }
        return (
            <SelectLeadStage
                value={lead.stage}
                onChange={(newStage) => handleChangeStageWithNudge(newStage, ELeadStageChangeReason.MANUAL_SELECT_NUDGE)}
                isWarning={isWarning}
            />
        );
    }, [notification, dispatchAction, smartRepliesVisible, updateLead, isWarning]);

    const secondaryAction = useMemo(() => {
        if (isWarning) {
            return null;
        }
        return <Button variant="contained" color={isWarning ? 'error' : 'primary'} onClick={() => dispatchAction(ESmartNotificationAction.OPEN_SNIPPETS)}>
            <Flex gap={1} alignItems="center">
                <SnippetsIcon width={15} height={15} />
                <Typography fontSize={14} fontWeight={600} component="div" color="white">
                    Open Snippets
                </Typography>
            </Flex>
        </Button>;
    }, [notification, dispatchAction, isWarning]);

    const scheduleTourAction = useMemo(() => {
        const hasUpcomingShowings = showings.some(o => o.lead_uuid === lead.uuid && dayjs(o.start_time).isAfter(dayjs()));
        const inLogicallyFinishedStage = [
            ELeadStage.NOT_QUALIFIED,
            ELeadStage.APPLICATION_COMPLETED,
            ELeadStage.NOT_INTERESTED,
            ELeadStage.LEASE_SIGNED
        ].includes(lead.stage);
        if (lead.stage == ELeadStage.CONTACTED || (!hasUpcomingShowings && !inLogicallyFinishedStage)) {
            return <Button variant="outlined" color={isWarning ? 'error' : 'primary'} onClick={() => dispatchAction(ESmartNotificationAction.OPEN_TOUR_MODAL)}>
                <Flex gap={1} alignItems="center">
                    <ShowingIcon width={15} height={15} />
                    <Typography fontSize={14} fontWeight={600} component="div" color="primary">
                        Schedule a Tour
                    </Typography>
                </Flex>
            </Button>;
        }
        return null;
    }, [lead, showings, isWarning]);

    if (hideSmartReplies && !notification) {
        return null;
    }

    if (!notification) {
        return <Box p={1} className={styles.outer}>
            <FlexColumn rowGap={1} className={styles.inner} justifyContent="space-between">
                <Flex alignItems="center" justifyItems="space-between" columnGap={2}>
                    {!notifMidsize && <img src="/img/conversation/box_logo.png" alt="image" width={36} height={36} />}

                    <SmartReplies onClick={setReplyInput} />
                </Flex>
            </FlexColumn>
        </Box>;
    }

    return <>
        {!isSmallScreen && <Box p={1} className={`${styles.outer} ${notifMidsize ? styles.outerLessLg : ''} ${notifMidsize2 ? styles.outerSmall : ''}`}>
            <FlexColumn rowGap={1} className={`${styles.inner} ${isWarning ? styles.warning : ''}`}
                justifyContent="space-between"
                maxWidth={'100%'}
            >
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex alignItems="center" justifyItems="space-between" columnGap={2}>
                        {!notifMidsize2 && <>
                            {isWarning ?
                                (<img src="/img/conversation/box_logo_red.png" alt="image" width={36} height={36} />) :
                                (<img src="/img/conversation/box_logo.png" alt="image" width={36} height={36} />)}
                        </>}
                        <FlexColumn flex={1} rowGap={0.5}>
                            <Typography fontSize={14} fontWeight={600} component="div">
                                {notification.title}
                            </Typography>
                            <Typography fontSize={12} fontWeight={400} component="div">
                                {notification.description}
                            </Typography>
                        </FlexColumn>
                    </Flex>
                    <Flex columnGap={2}>
                        {scheduleTourAction}
                        {secondaryAction}
                        {primaryAction}
                    </Flex>
                </Flex>


                {!hideSmartReplies && <div className={notifMidsize ? styles.smartRepliesSmall : styles.smartReplies}>
                    <SmartReplies onClick={setReplyInput} />
                </div>}
            </FlexColumn>
        </Box>}
        {!hideSmartReplies && isSmallScreen &&
            <Box p={1} className={styles.outer} style={{ visibility: smartReplies && smartReplies?.replies.length ? 'inherit' : 'hidden' }}>
                <FlexColumn rowGap={1} className={`${styles.inner} ${isWarning ? styles.warning : ''}`}
                    justifyContent="space-between"
                >
                    <div className={isSmallScreen ? styles.smartRepliesSmall : styles.smartReplies}>
                        <SmartReplies onClick={setReplyInput} />
                    </div>
                </FlexColumn>
            </Box>}
    </>;
};


